import { useMemo, useRef, useState } from "react";
import { Play, Pause, Trash2, Filter, Archive } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { IDS } from "../constants/testIds";
import { transmissionAudioUrl, deleteTransmission } from "../lib/api";
import { fmtTime, tzAbbrev } from "../lib/time";

const KIND_STYLE = {
  bot: "bg-slate-100 text-slate-600",
  direct: "bg-violet-50 text-violet-700",
  broadcast: "bg-amber-50 text-amber-700",
  patch: "bg-orange-50 text-orange-700",
  talkgroup: "bg-teal-50 text-teal-700",
  channel: "bg-blue-50 text-blue-700",
};

export default function AudioLogger({ transmissions, onDeleted, timezone = "UTC", speakerDeviceId = "", muted = false }) {
  const [filter, setFilter] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return transmissions;
    return transmissions.filter(t =>
      (t.sender_name || "").toLowerCase().includes(f) ||
      (t.channel_name || "").toLowerCase().includes(f) ||
      (t.kind || "").toLowerCase().includes(f) ||
      (t.transcript || t.message || "").toLowerCase().includes(f)
    );
  }, [transmissions, filter]);

  const play = (tx) => {
    if (!tx.has_audio) return;
    if (playingId === tx.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(transmissionAudioUrl(tx.id));
    audio.muted = muted;
    if (speakerDeviceId && typeof audio.setSinkId === "function") {
      audio.setSinkId(speakerDeviceId).catch(() => {});
    }
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(tx.id);
  };

  const remove = async (id) => {
    await deleteTransmission(id);
    if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); }
    onDeleted?.();
  };

  return (
    <section data-testid={IDS.logger.root} className="h-full rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white grid place-items-center">
            <Archive className="h-4 w-4" />
          </div>
          <div>
            <div className="overline">Audio Logger</div>
            <h2 className="text-base font-semibold tracking-tight">Transmission Archive</h2>
          </div>
        </div>
        <div className="flex items-center gap-2 min-w-[220px]">
          <Filter className="h-4 w-4 text-slate-400" />
          <Input
            data-testid={IDS.logger.filter}
            placeholder="Filter by sender, channel, transcript…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="h-9"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 z-10">
            <tr>
              <th className="text-left px-5 py-2 font-medium text-[11px] uppercase tracking-wider">Time ({tzAbbrev(timezone)})</th>
              <th className="text-left px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Sender</th>
              <th className="text-left px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Channel</th>
              <th className="text-left px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Kind</th>
              <th className="text-left px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Transcript</th>
              <th className="text-right px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Duration</th>
              <th className="text-right px-5 py-2 font-medium text-[11px] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400 text-xs">No transmissions match the filter.</td></tr>
            )}
            {rows.map(tx => (
              <tr key={tx.id} data-testid={IDS.logger.row(tx.id)} className="border-t border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-2 font-mono text-xs text-slate-600">{fmtTime(tx.started_at, timezone)}</td>
                <td className="px-3 py-2">{tx.sender_name}</td>
                <td className="px-3 py-2 text-slate-600">
                  {tx.channel_name || "—"}
                  {tx.kind === "patch" && <span className="block text-[10px] text-orange-600">via {tx.patch_name} ← {tx.patched_from}</span>}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${tx.tone ? "bg-yellow-50 text-yellow-800" : KIND_STYLE[tx.kind] || KIND_STYLE.channel}`}>{tx.tone ? "tone" : tx.kind}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[280px]">
                  <span data-testid={`logger-transcript-${tx.id}`} className="line-clamp-2 italic">{tx.transcript || tx.message || <span className="text-slate-300 not-italic">—</span>}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{(tx.duration_ms/1000).toFixed(1)}s</td>
                <td className="px-5 py-2">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      data-testid={IDS.logger.play(tx.id)}
                      onClick={() => play(tx)}
                      disabled={!tx.has_audio}
                      className={`p-1.5 rounded-md ${tx.has_audio ? "text-blue-700 hover:bg-blue-50" : "text-slate-300 cursor-not-allowed"}`}
                      aria-label="Play recording"
                    >
                      {playingId === tx.id ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => remove(tx.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-md" aria-label="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
